<template>
  <div class="">
    <div class="flex flex-col h-full w-full">
      <div class="flex gap-6 flex-1">
        <div
          class="bg-card rounded-xl border border-border p-6"
          style="width: 450px"
        >
          <div class="flex justify-between items-center gap-x-3 mb-4">
            <div class="relative flex-1">
              <Search
                class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground"
              />
              <Input
                v-model="searchQuery"
                :placeholder="$t('Поиск по дереву')"
                class="pl-9"
              />
            </div>

            <Button variant="outline" @click="showAddDialog(null)" size="icon">
              <svg-icon
                name="huge-plus"
                class="w-4 h-4 text-muted-foreground"
              />
            </Button>
          </div>

          <div
            class="tree-container"
            style="height: calc(100vh - 280px); overflow-y: auto"
          >
            <DirectoryTreeItem
              v-for="item in filteredTreeData"
              :key="item.code"
              :item="item"
              :depth="0"
              :selected-item-code="selectedItem?.code"
              :expanded-items="expandedItems"
              :children-data="childrenCache"
              @select="handleItemSelect"
              @edit="showEditDialog"
              @add-child="showAddDialog"
              @delete="deleteItem"
              @expand="handleTreeItemExpand"
              @collapse="handleTreeItemCollapse"
              @children-loaded="handleChildrenLoaded"
            />

            <div v-if="filteredTreeData.length === 0" class="text-center py-8">
              <div class="text-muted-foreground">
                {{ $t(searchQuery ? "Элементы не найдены" : "Нет элементов") }}
              </div>
            </div>
          </div>

          <div class="mt-4 pt-4 border-t border-border">
            <div class="text-sm text-foreground">
              {{ $t("Всего элементов") }}:
              <span class="font-medium">{{ totalItems }}</span>
            </div>
          </div>
        </div>

        <div
          v-if="selectedItem"
          class="w-full flex flex-col gap-4"
          style="height: calc(100vh - 100px); overflow-y: auto"
        >
          <DirectoryItem
            class="w-full"
            :selected-item="selectedItem"
            :loading="childrenLoading"
            :items="childrenData.items"
            :total="childrenData.total"
            :pagination="childrenPagination"
            @row-click="handleTableRowClick"
            @edit="showEditDialog"
            @exclude="excludeFromChildren"
            @delete="deleteItem"
            @page-change="handlePageChange"
            @add-child="showAddDialog"
          />
        </div>
        <div
          v-else
          class="bg-card rounded-xl border border-border p-6 flex-1 flex items-center justify-center"
        >
          <div class="text-center">
            <svg-icon
              name="huge-list-check"
              class="w-12 h-12 text-muted-foreground mx-auto mb-3"
            />
            <div class="text-foreground">
              {{ $t("Выберите элемент для просмотра деталей") }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <DirectoryItemDialog
      v-model="showEditDialogVisible"
      :item="currentEditItem"
      :parent-info="currentParentInfo"
      mode="edit"
      @saved="handleItemSaved"
    />

    <DirectoryItemDialog
      v-model="showAddDialogVisible"
      :parent-info="currentParentInfo"
      mode="add"
      @saved="handleItemSaved"
    />
  </div>
</template>

<script>
import { Search } from "lucide-vue-next";
import { Input } from "@/shared/ui/input";
import DirectoryTreeItem from "./components/DirectoryTreeItem.vue";
import DirectoryDrawer from "./components/DirectoryDrawer.vue";
import DirectoryItem from "@/pages/admin/references2/DirectoryItem.vue";
import Button from "@/shared/ui/button/Button.vue";
import {
  getDirectoryTree,
  getDirectoryList,
  getDirectoryItem,
  getDirectoryChildren,
  deleteDirectoryItem,
  updateDirectoryItem,
} from "@/entities/directory";

export default {
  name: "DirectoryTree",
  components: {
    DirectoryTreeItem,
    DirectoryItemDialog: DirectoryDrawer,
    Search,
    Input,
    Button,
    DirectoryItem,
  },

  data() {
    return {
      treeLoading: false,
      childrenLoading: false,

      treeData: [],
      selectedItem: null,
      searchQuery: "",
      searchResults: [],
      searchLoading: false,
      searchTimer: null,

      childrenData: {
        items: [],
        total: 0,
        page: 1,
        size: 20,
      },
      childrenPagination: {
        page: 1,
        size: 20,
      },

      showEditDialogVisible: false,
      showAddDialogVisible: false,
      currentEditItem: null,
      currentParentInfo: null,

      expandedItems: [],
      childrenCache: {},
    };
  },

  computed: {
    totalItems() {
      if (this.searchQuery) return this.searchResults.length;
      const countNodes = (nodes) => {
        return nodes.reduce((total, node) => {
          return total + 1 + (node.children ? countNodes(node.children) : 0);
        }, 0);
      };
      return countNodes(this.treeData);
    },

    filteredTreeData() {
      if (this.searchQuery) return this.searchResults;
      return this.treeData;
    },
  },

  watch: {
    searchQuery(query) {
      clearTimeout(this.searchTimer);
      if (!query) {
        this.searchResults = [];
        return;
      }
      this.searchTimer = setTimeout(() => {
        this.searchDirectory(query);
      }, 300);
    },
    selectedItem(newVal) {
      if (newVal) {
        this.loadChildren();
      } else {
        this.childrenData.items = [];
        this.childrenData.total = 0;
      }
    },
    childrenPagination: {
      handler() {
        if (this.selectedItem) {
          this.loadChildren();
        }
      },
      deep: true,
    },
  },

  mounted() {
    this.loadRootItems();
  },

  methods: {
    async searchDirectory(query) {
      this.searchLoading = true;
      try {
        const data = await getDirectoryList({ q: query, page: 1, size: 100 });
        if (data?.success && data.payload) {
          this.searchResults = data.payload.items || [];
        } else {
          this.searchResults = [];
        }
      } catch (error) {
        console.error("Ошибка поиска:", error);
        this.searchResults = [];
      } finally {
        this.searchLoading = false;
      }
    },

    async loadRootItems() {
      this.treeLoading = true;
      try {
        const data = await getDirectoryTree({
          page: 1,
          size: 100,
        });

        if (data?.success && data.payload) {
          this.treeData = data.payload.items;
        }
      } catch (error) {
        console.error("Ошибка загрузки корневых элементов:", error);
        this.$message.error(this.$t("Не удалось загрузить дерево"));
      } finally {
        this.treeLoading = false;
      }
    },

    async loadChildren() {
      if (!this.selectedItem) {
        this.childrenData.items = [];
        this.childrenData.total = 0;
        return;
      }

      this.childrenLoading = true;
      try {
        const data = await getDirectoryChildren(this.selectedItem.code, {
          page: this.childrenPagination.page,
          size: this.childrenPagination.size,
        });

        if (data?.success && data.payload) {
          this.childrenData.items = data.payload.items;
          this.childrenData.total = data.payload.total;
          this.childrenData.page = data.payload.page;
          this.childrenData.size = data.payload.size;
        } else {
          this.childrenData.items = [];
          this.childrenData.total = 0;
        }
      } catch (error) {
        console.error("Ошибка загрузки дочерних элементов:", error);
        this.childrenData.items = [];
        this.childrenData.total = 0;
      } finally {
        this.childrenLoading = false;
      }
    },

    async loadItemDetails(code) {
      try {
        const data = await getDirectoryItem(code);
        if (data?.success && data.payload) {
          this.selectedItem = data.payload;
        }
      } catch (error) {
        console.error("Ошибка загрузки деталей:", error);
      }
    },
    handleItemSelect(item) {
      this.selectedItem = item;
      this.childrenPagination.page = 1;
      this.ensureParentExpanded(item);
    },

    handleTableRowClick(row) {
      this.selectedItem = row;
    },

    showEditDialog(item) {
      this.currentEditItem = { ...item };
      this.currentParentInfo = null;
      this.showEditDialogVisible = true;
    },

    showAddDialog(parentItem) {
      this.currentEditItem = null;
      this.currentParentInfo = parentItem || null;
      this.showAddDialogVisible = true;
    },

    async excludeFromChildren(item) {
      try {
        await this.$confirm(
          this.$t("Вы уверены, что хотите исключить элемент из списка?"),
          this.$t("Внимание"),
          {
            confirmButtonText: this.$t("Да"),
            cancelButtonText: this.$t("Отмена"),
          }
        );

        const updateData = {
          ...item,
          parent_code: null,
        };

        await updateDirectoryItem(item.code, updateData);
        this.$message.success(this.$t("Элемент исключен из списка"));

        await this.refreshTree();

        if (this.selectedItem?.code === item.parent_code) {
          await this.loadChildren();
          await this.refreshChildrenCache(this.selectedItem.code);
        }
      } catch (error) {
        if (error !== "cancel") {
          console.error("excludeFromChildren error", error);
          this.$message.error(this.$t("Не удалось исключить элемент"));
        }
      }
    },

    async deleteItem(item) {
      try {
        await this.$confirm(
          this.$t("Вы уверены, что хотите удалить элемент?"),
          this.$t("Внимание"),
          {
            confirmButtonText: this.$t("Да"),
            cancelButtonText: this.$t("Отмена"),
          }
        );

        const data = await deleteDirectoryItem(item.code);
        if (data?.success) {
          this.$message.success(this.$t("Элемент удален"));

          const parentCode = item.parent_code;

          await this.refreshTree();

          if (parentCode) {
            await this.refreshChildrenCache(parentCode);
          }

          if (this.selectedItem?.code === item.code) {
            this.selectedItem = null;
          }
        } else {
          this.$message.error(
            data?.message || this.$t("Не удалось удалить элемент")
          );
        }
      } catch (error) {
        if (error !== "cancel") {
          console.error("Ошибка удаления:", error);
          this.$message.error(this.$t("Не удалось удалить элемент"));
        }
      }
    },

    async handleItemSaved() {
      const parentCode = this.currentParentInfo?.code;
      const editCode = this.currentEditItem?.code;
      const currentSelectedCode = this.selectedItem?.code;

      try {
        await this.refreshTree();

        if (currentSelectedCode) {
          await this.loadItemDetails(currentSelectedCode);
        }

        if (parentCode && currentSelectedCode === parentCode) {
          await this.refreshChildrenCache(parentCode);
          await this.loadChildren();

          if (!this.expandedItems.includes(parentCode)) {
            this.expandedItems.push(parentCode);
          }
        }

        if (editCode && currentSelectedCode === editCode) {
          await this.loadItemDetails(editCode);
        }
      } catch (error) {
        console.error("Error in handleItemSaved:", error);
      } finally {
        this.currentEditItem = null;
        this.currentParentInfo = null;
      }
    },
    handleTreeItemExpand(itemCode) {
      if (!this.expandedItems.includes(itemCode)) {
        this.expandedItems.push(itemCode);
      }
    },

    handleTreeItemCollapse(itemCode) {
      const index = this.expandedItems.indexOf(itemCode);
      if (index > -1) {
        this.expandedItems.splice(index, 1);
      }
    },

    handleChildrenLoaded({ parentCode, children }) {
      this.childrenCache[parentCode] = children;
    },

    ensureParentExpanded(item) {
      if (!item?.parent_code) return;

      const findParent = (items, parentCode) => {
        for (const node of items) {
          if (node.code === parentCode) return node;
          if (node.children) {
            const found = findParent(node.children, parentCode);
            if (found) return found;
          }
        }
        return null;
      };

      const parent = findParent(this.treeData, item.parent_code);
      if (parent && !this.expandedItems.includes(parent.code)) {
        this.expandedItems.push(parent.code);
        this.ensureParentExpanded(parent);
      }
    },

    handlePageChange(page) {
      this.childrenPagination.page = page;
    },
    async refreshTree() {
      try {
        const previouslyExpanded = [...this.expandedItems];

        await this.loadRootItems();

        if (this.selectedItem) {
          await this.loadItemDetails(this.selectedItem.code);
        }

        this.expandedItems = previouslyExpanded;

        const cacheToReload = Object.keys(this.childrenCache).filter((code) =>
          this.expandedItems.includes(code)
        );

        this.childrenCache = {};

        for (const code of cacheToReload) {
          await this.refreshChildrenCache(code);
        }
      } catch (error) {
        console.error("Error refreshing tree:", error);
      }
    },

    async refreshChildrenCache(parentCode) {
      try {
        const data = await getDirectoryChildren(parentCode, {
          page: 1,
          size: 100,
        });

        if (data?.success && data.payload?.items) {
          this.childrenCache[parentCode] = data.payload.items;
        }
      } catch (error) {
        console.error(`Ошибка обновления кэша для ${parentCode}:`, error);
      }
    },
  },
};
</script>

<style scoped lang="scss">
.tree-container {
  scrollbar-width: thin;
  scrollbar-color: #cbd5e1 #f8fafc;
}

.tree-container::-webkit-scrollbar {
  width: 6px;
}

.tree-container::-webkit-scrollbar-track {
  background: #f8fafc;
}

.tree-container::-webkit-scrollbar-thumb {
  background-color: #cbd5e1;
  border-radius: 3px;
}
</style>
